import { useLayoutEffect, useState } from 'react';

import { copyTreeToNodes } from '../../utils/copy-tree-to-nodes';
import { determineFurthestFromViewportRectangle } from '../../utils/determineFurthestFromViewportRectangle';
import { InteractableMap } from './use-interactable-map';
import { Positioning } from './use-mindmap-positioning';

const BORDER_GAP = 24;

type MoveIntoViewportOptions = {
  /**
   * If false the nodes get moved into the viewport after the next positionedTree update.
   *
   * If true the nodes get moved into the viewport directly.
   *
   * Should not be changed from default (=false) when setNodeMap is called in the same lifecycle-phase to prevent wrong movements.
   * Can be set to true if the nodeMap is not changed in the same lifecycle-phase, meaning the positioning of nodes won't be changed in this lifecycle-phase.
   */
  instant?: boolean;
};

export interface Viewport {
  moveIntoViewport: (ids: string[], options?: MoveIntoViewportOptions) => void;
}

type UseViewport = { interactableMap: InteractableMap; positioning: Positioning };

export type Rect = { top: number; bottom: number; left: number; right: number };

export const useMindmapViewport = ({
  interactableMap: { interactableMapSpring, mapCoordsToPageClientCoords, updateStyle },
  positioning: { positionedTree },
}: UseViewport): Viewport => {
  const [moveToIdsBatches, setMoveToIdsBatches] = useState<string[][]>([]);

  const moveIntoViewport = (ids: string[], options?: MoveIntoViewportOptions) => {
    if (options?.instant) {
      moveNodesIntoViewport(ids);
    } else {
      // move after the new map is rendered on screen
      setMoveToIdsBatches((moveIds) => [...moveIds, ids]);
    }
  };

  useLayoutEffect(() => {
    if (moveToIdsBatches.length > 0 && interactableMapSpring && positionedTree) {
      const ids = moveToIdsBatches.shift();
      if (ids && ids.length > 0) {
        // remove this batch of ids from moveIds
        setMoveToIdsBatches((moveIds) => {
          moveIds.shift();
          return moveIds;
        });
        moveNodesIntoViewport(ids);
      }
    }
  }, [positionedTree]);

  const moveNodesIntoViewport = (ids: string[]) => {
    if (positionedTree && interactableMapSpring) {
      // get an array with all nodes and their positions on the map
      const flatPositionedNodes = copyTreeToNodes([positionedTree], {}, { keepIds: true });

      // get nodes actual position/rect on screen
      const boundingClientRects: Rect[] = flatPositionedNodes
        .filter(({ id }) => ids.includes(id))
        .map((node) => {
          if (node.x != null && node.y != null) {
            const topLeft = mapCoordsToPageClientCoords(node.x, node.y);
            const bottomRight = mapCoordsToPageClientCoords(node.x + node.width, node.y + node.height);
            return {
              top: topLeft?.y,
              left: topLeft?.x,
              bottom: bottomRight?.y,
              right: bottomRight?.x,
            };
          }
        })
        .filter((rect): rect is Rect => !!rect);

      const { moveXAmount, moveYAmount } = determineFurthestFromViewportRectangle(boundingClientRects);

      if (moveXAmount != 0 || moveYAmount != 0) {
        const borderX = moveXAmount < 0 ? -BORDER_GAP : BORDER_GAP;
        const borderY = moveYAmount < 0 ? -BORDER_GAP : BORDER_GAP;

        const newX = moveXAmount == 0 ? undefined : interactableMapSpring[0].x.goal + moveXAmount + borderX;
        const newY = moveYAmount == 0 ? undefined : interactableMapSpring[0].y.goal + moveYAmount + borderY;
        updateStyle({ x: newX, y: newY, animate: true });
      }
    }
  };

  return { moveIntoViewport };
};
